/* nx-first-order.js — the first-order code does what a tap on it promises.
 *
 * Since 2026-10-04 the code is WELCOME10 (10%, the only rate that keeps the $10 price grid whole) and it
 * lives on the product page as a band under the delivery line (.nxfo, sections/nx-product-hero.liquid): the
 * whole band is the button, its words change and its Apply pill goes once the code is on the cart. The
 * notes below were written for WELCOME15 and the 20 / 25% ladder; the logic holds for 10% and 20 / 30%.
 *
 * The line "15% off your first order · code WELCOME15" is set in clay with a dashed rule, which
 * reads as something to press — and people pressed it. In Clarity the owner watched them tap it,
 * wait, and tap again, because nothing happened (2026-09-21). A tap now puts the code on the cart
 * and the line says, truthfully, what that did.
 *
 * ⚠ TRUTHFULLY, because the code does not always help. WELCOME15 does not combine with the
 * quantity ladder (automatic discounts "Objects · 2 → 20%", "3+ → 25%"), and Shopify
 * answers with `applicable: false` once two or more objects are in the cart. Measured on the live
 * cart, 2026-09-22: one item $89.00 → $75.65 with the code; two items stay $151.30 and three stay
 * $213.60, where the ladder already does as well or better. So there are two answers, not one:
 *   · empty cart or one object  → "✓ 15% off applied" — an empty cart keeps the code and it lands
 *                                 on the first object added (measured: $75.65, applicable: true)
 *   · two objects or more       → "Your 2 objects already take 20% off", the percentage read from
 *                                 what the cart actually discounted, not from a table here
 * Telling a three-object cart that its code was "applied" would be a promise the checkout breaks.
 *
 * ⚠ It does not decide who is new. WELCOME15 applies once per customer and Shopify enforces that
 * at checkout, by email — a cookie on this page cannot know it, and a guess would refuse the
 * wrong people. The owner kept "once per customer" over "first order only" (2026-09-22).
 *
 * ⚠ The line's wording is not touched until the tap. "15% off your first order" is word for word
 * the Merchant Center promotion, and the ad, the page and the cart have to say one thing.
 */
(function () {
  if (window.__nxFirstOrder) return;
  window.__nxFirstOrder = true;

  var LINES = '.nx-product-hero__first-order, .nxca--first, .nxfo, .nxca--code:not([data-nx-first-order])';
  var CODES = '.nx-product-hero__first-order-code, .nxca__code, .nxfo__code';

  function codeOf(line) {
    var c = line.querySelector(CODES);
    return c ? c.textContent.trim() : '';
  }

  // The promise's own number, from the line Liquid printed — one source for the percentage.
  function promisedPercent(line) {
    var m = (line.dataset.nxOriginal || line.textContent).match(/(\d+)\s*%/);
    return m ? m[1] : '';
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch];
    });
  }

  /* What the cart says about this code. null when the code is not on the cart at all. */
  function stateOf(cart, code) {
    var entry = (cart.discount_codes || []).filter(function (d) {
      return String(d.code).toUpperCase() === String(code).toUpperCase();
    })[0];
    if (!entry) return null;
    if (entry.applicable || !cart.item_count) return { kind: 'applied' };
    var off = cart.original_total_price
      ? Math.round((cart.total_discount / cart.original_total_price) * 100)
      : 0;
    if (cart.item_count >= 2 && off > 0) return { kind: 'ladder', count: cart.item_count, off: off };
    return { kind: 'unknown' };
  }

  /* A band (the product page's .nxfo) keeps its picture, its kicker and its pill; only its words change, in
     [data-nx-fo-text]. A bare line is its own words. */
  function textOf(line) { return line.querySelector('[data-nx-fo-text]') || line; }

  function paintLine(line, state) {
    var code = codeOf(line) || line.dataset.nxCode || '';
    var words = textOf(line);
    if (!line.dataset.nxOriginal) {
      line.dataset.nxOriginal = words.textContent.trim();
      line.dataset.nxCode = code;
      line.dataset.nxHtml = words.innerHTML;
    }
    if (!state || state.kind === 'unknown') {
      words.innerHTML = line.dataset.nxHtml;
      armLine(line);
      return;
    }
    line.removeAttribute('role');
    line.removeAttribute('tabindex');
    line.removeAttribute('aria-label');
    line.setAttribute('data-nx-first-order', state.kind);
    if (state.kind === 'applied') {
      words.innerHTML =
        '<span class="nx-first-order__on" aria-hidden="true">✓</span> ' +
        esc(promisedPercent(line) || '10') + '% off applied · ' +
        '<span class="nx-first-order__on">' + esc(line.dataset.nxCode) + '</span>';
    } else {
      words.innerHTML =
        'Your ' + state.count + ' objects already take ' + state.off + '% off';
    }
  }

  /* THE LADDER LINE NAMES THE CODE IT OVERRULED (owner, 2026-10-07). With the code on the cart and two objects
     or more, Shopify keeps the code and does not apply it: it never combines with the quantity ladder. The drawer
     and /cart said nothing of it, and the code typed again at checkout read "WELCOME10 couldn't be used with your
     existing discounts" (the $224 basket of 06.10 went into checkout twice and left). snippets/nx-cart-ladder.liquid
     prints the code, its rate and the next step on .nxcl; the ladder's rate here is what the cart actually took
     off, not the table. No code, or a code that works: the line stays as Liquid printed it. */
  var LADDER = '.nxcl[data-nx-fo-code]';
  function ladderRate(cart) {
    var auto = (cart.cart_level_discount_applications || []).filter(function (d) {
      return d.value_type === 'percentage' && d.type !== 'discount_code';
    })[0];
    if (auto) return Math.round(parseFloat(auto.value));
    return cart.original_total_price ? Math.round((cart.total_discount / cart.original_total_price) * 100) : 0;
  }
  function paintLadder(cart) {
    document.querySelectorAll(LADDER).forEach(function (box) {
      var line = box.querySelector('.nxcl__line');
      if (!line) return;
      if (!box.dataset.nxHtml) box.dataset.nxHtml = line.innerHTML;
      var code = box.getAttribute('data-nx-fo-code');
      var entry = (cart.discount_codes || []).filter(function (d) {
        return String(d.code).toUpperCase() === String(code).toUpperCase();
      })[0];
      var rate = ladderRate(cart);
      var html = box.dataset.nxHtml;
      if (entry && !entry.applicable && cart.item_count >= 2 && rate > 0) {
        var next = parseInt(box.getAttribute('data-nx-next'), 10) || 0;
        var more = 'more than ' + esc(code) + "'s " + esc(box.getAttribute('data-nx-fo-pct') || '10') + '%';
        html = next > rate
          ? '<b>' + rate + '% off</b> is on, ' + more + '. One more object makes it ' + next + '%.'
          : '<b>' + rate + '% off</b> every object, ' + more + '.';
      }
      if (line.innerHTML !== html) line.innerHTML = html;
      if (html === box.dataset.nxHtml) box.removeAttribute('data-nx-overruled');
      else box.setAttribute('data-nx-overruled', code);
    });
  }

  function armLine(line) {
    if (line.getAttribute('role') === 'button') return;
    line.setAttribute('role', 'button');
    line.setAttribute('tabindex', '0');
    line.setAttribute('aria-label', 'Apply code ' + (codeOf(line) || line.dataset.nxCode || '') + ' to your cart');
    line.setAttribute('aria-live', 'polite');
    line.removeAttribute('data-nx-first-order');
  }

  function track(kind) {
    try { if (window.gtag) window.gtag('event', 'nx_first_order_tap', { result: kind }); } catch (e) {}
    try { if (window.clarity) window.clarity('event', 'first_order_' + kind); } catch (e) {}
  }

  var busy = false;
  async function apply(line) {
    if (busy) return;
    var code = codeOf(line) || line.dataset.nxCode;
    if (!code) return;
    busy = true;
    line.setAttribute('aria-busy', 'true');
    try {
      var res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: code,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname
        })
      });
      if (!res.ok) throw new Error('update ' + res.status);
      var cart = await res.json();
      // The drawer's totals change with the code; repaint it so no stale price is left standing.
      if (cart.sections) repaintDrawer(cart.sections);
      var state = stateOf(cart, code);
      document.querySelectorAll(LINES).forEach(function (l) { paintLine(l, state); });
      paintLadder(cart);
      track(state ? state.kind : 'none');
    } catch (e) {
      // No claim we cannot back. Hand the code over instead, so the checkout still gets it — and say
      // "copied" only if the clipboard actually took it.
      var copied = false;
      try { await navigator.clipboard.writeText(code); copied = true; } catch (err) {}
      line.textContent = copied ? 'Code ' + code + ' copied · enter it at checkout' : 'Enter ' + code + ' at checkout';
      track('failed');
    } finally {
      busy = false;
      line.removeAttribute('aria-busy');
    }
  }

  function repaintDrawer(sections) {
    if (window.nxCart && window.nxCart.paint) { window.nxCart.paint(sections); return; }
    if (!sections['cart-drawer']) return;
    var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
    var fresh = doc.querySelector('#CartDrawer');
    var live = document.querySelector('#CartDrawer');
    if (fresh && live) live.innerHTML = fresh.innerHTML;
  }

  document.addEventListener('click', function (e) {
    var line = e.target.closest && e.target.closest(LINES);
    if (!line || line.getAttribute('role') !== 'button') return;
    e.preventDefault();
    apply(line);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var line = e.target.closest && e.target.closest(LINES);
    if (!line || line.getAttribute('role') !== 'button') return;
    e.preventDefault();
    apply(line);
  });

  /* Read the cart once on arrival, and again whenever the drawer is repainted — Shopify replaces
     its markup on every add, which prints a fresh, un-armed line and may change what the code is
     worth (a second object turns "applied" into the ladder's answer). */
  var pending = null;
  async function sync() {
    var lines = document.querySelectorAll(LINES);
    // with two objects there is no code band in the drawer, and the ladder line still needs the cart
    if (!lines.length && !document.querySelector(LADDER)) return;
    lines.forEach(function (l) { if (!l.dataset.nxOriginal) armLine(l); });
    try {
      var cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      lines.forEach(function (l) {
        var code = codeOf(l) || l.dataset.nxCode;
        var state = stateOf(cart, code);
        if (state) paintLine(l, state);
        else if (l.dataset.nxOriginal) paintLine(l, null);
      });
      paintLadder(cart);
    } catch (e) {}
  }
  function soon() {
    clearTimeout(pending);
    pending = setTimeout(sync, 150);
  }

  function start() {
    sync();
    if (!window.MutationObserver) return;
    // Our own repaint of a line or of the ladder's words is a mutation too; only new markup counts.
    var OURS = LINES + ', .nxcl';
    function watch(root) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          if (records[i].addedNodes.length && !records[i].target.closest(OURS)) { soon(); return; }
        }
      }).observe(root, { childList: true, subtree: true });
    }
    var drawer = document.querySelector('#CartDrawer') || document.querySelector('cart-drawer');
    if (drawer) watch(drawer);
    // /cart repaints its footer (the ladder) through the Section Rendering API, outside the drawer
    var main = document.getElementById('MainContent');
    if (main && main.querySelector(LADDER)) watch(main);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
